import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import debounce from "lodash/debounce";
import {
  Table,
  Checkbox,
  message,
  Card,
  Row,
  Col,
  Input,
  Button,
  Spin,
  Select,
} from "antd";
import { API_URL } from "../../url_config";
import { NavigationStepsCapJour } from "../../components/navbars/navbar_cap_jour";
import { EditOutlined, ReloadOutlined } from "@ant-design/icons";
import ModifyItemPage from "../../components/modify_page";

const { TextArea } = Input;

// === Configuration des titres de colonnes ===
const columnTitles = {
  ligne: "Ligne",
  coquille: "Coquille à vérifier",
  configuration_ad: "Configuration AD SAE/VE3",
  remontee_de_ad_sae_tr: "Remontée AD SAE-TR",
  geolocalisation: "Géolocalisation AD SAE",
  gestion_ad: "Acquittement / Clôture SAE",
  remontee_de_ad_tetra: "Remontée AD TETRA",
  ecoute_d_ambiance: "Écoute d’ambiance",
  prise_en_compte_appel: "Appel individuel",
  signal: "ÉTAT",
};

// === Styles tableau ===
const tableStyle = document.createElement("style");
tableStyle.innerHTML = `
  .custom-row td {
    border-bottom: 2px solid #d9d9d9 !important;
  }

  .ant-table-thead > tr > th {
    background-color: #f5f5f5 !important;
    font-weight: 600;
    border-bottom: 2px solid #bfbfbf !important;
    color: #333;
  }

  .ant-table-tbody > tr:hover > td {
    background-color: #f0f0f0 !important;
  }

  .ant-table {
    border-collapse: separate !important;
    border-spacing: 0;
  }
`;
document.head.appendChild(tableStyle);

// === Zone texte fluide ===
const EditableTextArea = ({ recordId, field, initialValue, onPatch }) => {
  const [value, setValue] = useState(initialValue || "");

  useEffect(() => setValue(initialValue || ""), [initialValue]);

  const debouncedSave = useCallback(
    debounce((val) => onPatch(recordId, field, val), 800),
    [recordId, field, onPatch]
  );

  const handleChange = (e) => {
    const val = e.target.value;
    setValue(val);
    debouncedSave(val);
  };

  return (
    <TextArea
      rows={5}
      value={value}
      onChange={handleChange}
      placeholder="Saisissez une remarque..."
    />
  );
};

// === Champ input fluide (Coquille) ===
const EditableInput = ({ recordId, field, initialValue, onPatch }) => {
  const [value, setValue] = useState(initialValue || "");

  useEffect(() => setValue(initialValue || ""), [initialValue]);

  const debouncedSave = useCallback(
    debounce((val) => onPatch(recordId, field, val), 800),
    [recordId, field, onPatch]
  );

  const handleChange = (e) => {
    const val = e.target.value;
    setValue(val);
    debouncedSave(val);
  };

  return (
    <Input
      value={value}
      onChange={handleChange}
      placeholder="Saisir une coquille..."
      style={{ width: "100%", borderRadius: "6px" }}
    />
  );
};

// === Ligne développée ===
function TableGeolocalisation({ record, onPatch }) {
  return (
    <div style={{ padding: "20px" }}>
      <Row gutter={16}>
        <Col span={12}>
          <Card title="Commentaire" bordered>
            <EditableTextArea
              recordId={record.id}
              field="comment"
              initialValue={record.comment}
              onPatch={onPatch}
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card title="Informations (Supervision)" bordered>
            <EditableTextArea
              recordId={record.id}
              field="aide"
              initialValue={record.aide}
              onPatch={onPatch}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}

// === Composant principal ===
const ValidationGeolocalisationCapJour = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // === Chargement des données ===
  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/validations/validation_geolocalisation/?ot_tag=cap_jour`
      );
      const formatted = res.data.map((item, index) => ({
        key: item.id || index,
        id: item.id,
        ligne: item.ligne,
        coquille: item.coquille,
        configuration_ad: item.configuration_ad,
        remontee_de_ad_sae_tr: item.remontee_de_ad_sae_tr,
        geolocalisation: item.geolocalisation,
        gestion_ad: item.gestion_ad,
        remontee_de_ad_tetra: item.remontee_de_ad_tetra,
        ecoute_d_ambiance: item.ecoute_d_ambiance,
        prise_en_compte_appel: item.prise_en_compte_appel,
        comment: item.comment,
        aide: item.aide,
        signal: item.signal,
      }));
      setData(formatted);
    } catch (error) {
      console.error(error);
      message.error("Erreur de chargement des données");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // === PATCH fluide ===
  const debouncedPatch = useCallback(
    debounce(async (id, field, value) => {
      try {
        await axios.patch(
          `${API_URL}/validations/validation_geolocalisation/${id}/`,
          { [field]: value }
        );
      } catch (error) {
        console.error(error);
        message.error("Erreur lors de la mise à jour");
      }
    }, 400),
    []
  );

  // === Mise à jour locale instantanée ===
  const handleLocalChange = (recordId, field, value) => {
    setData((prev) =>
      prev.map((item) =>
        item.id === recordId ? { ...item, [field]: value } : item
      )
    );
    debouncedPatch(recordId, field, value);
  };

  // === Colonnes ===
  const columns = [
    { title: columnTitles.ligne, dataIndex: "ligne" },

    {
      title: columnTitles.configuration_ad,
      dataIndex: "configuration_ad",
      render: (val, record) => (
        <Select
          value={val || ""}
          onChange={(v) => handleLocalChange(record.id, "configuration_ad", v)}
          style={{
            width: 120,
            color: val === "ok" ? "#52c41a" : val === "ko" ? "#ff4d4f" : "#000",
            fontWeight: 600,
          }}
          options={[
            { value: "", label: " " },
            { value: "ok", label: <span style={{ color: "#52c41a" }}>OK</span> },
            { value: "ko", label: <span style={{ color: "#ff4d4f" }}>KO</span> },
          ]}
          dropdownStyle={{ textAlign: "center" }}
        />
      ),
    },
    {
      title: columnTitles.coquille,
      dataIndex: "coquille",
      render: (val, record) => (
        <EditableInput
          recordId={record.id}
          field="coquille"
          initialValue={val}
          onPatch={handleLocalChange}
        />
      ),
    },
    ...[
      "remontee_de_ad_sae_tr",
      "geolocalisation",
      "gestion_ad",
      // "remontee_de_ad_tetra",
      // "ecoute_d_ambiance",
      // "prise_en_compte_appel",
    ].map((field) => ({
      title: columnTitles[field],
      dataIndex: field,
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) => handleLocalChange(record.id, field, e.target.checked)}
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
            transform: "scale(1.3)",
            cursor: "pointer",
            accentColor: "#007bff",
          }}
        />
      ),
    })),
    {
      title: columnTitles.signal,
      dataIndex: "signal",
      render: (val, record) => {
        const color = val === 1 ? "#4CAF50" : val === -1 ? "#FF4D4F" : "#BFBFBF";
        const symbol = val === 1 ? "✓" : val === -1 ? "✗" : " ";
        const handleClick = () => {
          const nextVal = val === 0 ? 1 : val === 1 ? -1 : 0;
          handleLocalChange(record.id, "signal", nextVal);
        };
        return (
          <div onClick={handleClick} style={{ cursor: "pointer", display: "flex", justifyContent: "center", alignItems: "center" }}>
            <div style={{
              width: 28,
              height: 28,
              borderRadius: "6px",
              backgroundColor: color,
              color: "white",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontWeight: "bold",
              fontSize: "16px",
              userSelect: "none",
              transition: "all 0.3s ease",
            }}>
              {symbol}
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div style={{ padding: "20px", position: "relative" }}>
      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(255,255,255,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <Spin size="large" tip="Chargement..." />
        </div>
      )}

      <NavigationStepsCapJour selectedKey="geolocalisation" />

      <Button
        type="primary"
        icon={<ReloadOutlined />}
        onClick={fetchData}
        style={{
          position: "fixed",
          top: 20,
          right: 20,
          zIndex: 10000,
        }}
      >
        Rafraîchir
      </Button>

      <Table
        columns={columns}
        dataSource={data}
        bordered
        style={{
          border: "2px solid #bfbfbf",
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          marginTop: 60,
        }}
        rowClassName={() => "custom-row"}
        pagination={false}
        expandable={{
          expandedRowRender: (record) => (
            <TableGeolocalisation record={record} onPatch={handleLocalChange} />
          ),
        }}
      />

      <div style={{ padding: 20 }}>
        <ModifyItemPage
          apiUrl={`${API_URL}/validations/validation_geolocalisation/`}
          ot_tag="cap_jour"
          title="Gestion"
          rowKey="id"
          trigger={<Button icon={<EditOutlined />} shape="circle" />}
          columns={[{ title: columnTitles.ligne, dataIndex: "ligne", key: "ligne" }]}
          formFields={[{ name: "ligne", label: columnTitles.ligne, placeholder: "Entrez la ligne" }]}
        />
      </div>
    </div>
  );
};

export default ValidationGeolocalisationCapJour;
